/**
 * stockType 工具函数
 *
 * 从路由获取股票市场类型，支持服务端和客户端使用
 * 路由格式：/[locale]/[device]/(root)/(maintab)/discover/[market]
 * market 参数：us-stocks | hk-stocks | crypto
 */

export type StockType = "US" | "HK";

/**
 * 路由 market 参数到 StockType 的映射
 */
const MARKET_TO_STOCK_TYPE: Record<string, StockType> = {
  "us-stocks": "US",
  "hk-stocks": "HK",
};

/**
 * 默认股票市场类型
 */
export const DEFAULT_STOCK_TYPE: StockType = "US";

/**
 * 从路由 market 参数获取 StockType
 *
 * @param market - 路由中的 market 参数（us-stocks | hk-stocks | crypto）
 * @returns StockType（US | HK），如果是 crypto 或无效值则返回默认值 US
 *
 * @example
 * getStockTypeFromMarket("us-stocks") // "US"
 * getStockTypeFromMarket("hk-stocks") // "HK"
 * getStockTypeFromMarket("crypto")    // "US" (默认值)
 */
export function getStockTypeFromMarket(
  market: string | undefined | null,
): StockType {
  if (!market) {
    return DEFAULT_STOCK_TYPE;
  }
  return MARKET_TO_STOCK_TYPE[market] ?? DEFAULT_STOCK_TYPE;
}

/**
 * 从 pathname 中解析 StockType
 *
 * @param pathname - 完整的 pathname（如 /en/app/discover/us-stocks）
 * @returns StockType（US | HK），如果无法解析则返回默认值 US
 *
 * @example
 * getStockTypeFromPathname("/en/app/discover/us-stocks") // "US"
 * getStockTypeFromPathname("/en/app/discover/hk-stocks") // "HK"
 * getStockTypeFromPathname("/en/app/discover/crypto")    // "US" (默认值)
 */
export function getStockTypeFromPathname(
  pathname: string | undefined | null,
): StockType {
  if (!pathname) {
    return DEFAULT_STOCK_TYPE;
  }

  // 检查 pathname 是否包含 market 标识
  if (pathname.includes("/hk-stock")) {
    return "HK";
  }
  if (pathname.includes("/us-stock")) {
    return "US";
  }

  return DEFAULT_STOCK_TYPE;
}
